

/**
 * Comparison Table Component
 * Side-by-side vendor comparison with metrics
 */

import { useMemo } from 'react';
import { Star, Check, X, Award, Leaf, Truck, Home, Calendar } from 'lucide-react';
import { ComparisonTableData, ComparisonRow } from '@/types/comparison';
import { MetricCell } from './metric-cell';
import { cn } from '@/lib/utils';

interface ComparisonTableProps {
  data: ComparisonTableData;
  showHeaders?: boolean;
  highlightDifferences?: boolean;
  className?: string;
}

export function ComparisonTable({
  data,
  showHeaders = true,
  highlightDifferences = true,
  className,
}: ComparisonTableProps) {
  const { items, rows } = data;

  // Calculate which values are different for highlighting
  const differentIndices = useMemo(() => {
    if (!highlightDifferences || items.length < 2) return new Set<number>();
    
    const different = new Set<number>();
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const values = rows[rowIndex].values;
      const firstValue = JSON.stringify(values[0]);
      for (let i = 1; i < values.length; i++) {
        if (JSON.stringify(values[i]) !== firstValue) {
          different.add(rowIndex);
          break;
        }
      }
    }
    return different;
  }, [rows, items.length, highlightDifferences]);

  return (
    <div className={cn('overflow-auto', className)}>
      <table className="w-full border-collapse min-w-[600px]">
        <thead>
          {showHeaders && (
            <tr>
              <th className="sticky left-0 bg-background z-10 p-4 text-left border-b min-w-[150px]">
                <span className="text-sm font-medium text-muted-foreground">Farm</span>
              </th>
              {items.map((item) => (
                <th key={item.id} className="p-4 text-center border-b min-w-[180px]">
                  <div className="flex flex-col items-center gap-2">
                    <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                      {item.data.logo ? (
                        <img
                          src={item.data.logo}
                          alt={item.data.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-xl font-bold text-primary">
                          {item.data.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="font-semibold text-center">
                      {item.data.name}
                    </div>
                    <div className="text-sm text-muted-foreground text-center">
                      {item.data.location.city}
                    </div>
                    {item.data.verified && (
                      <div className="flex items-center gap-1 text-xs text-forest">
                        <Award className="h-3 w-3" />
                        Verified
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={row.metric.key}
              className={cn(
                'border-b',
                differentIndices.has(rowIndex) && 'bg-muted/30'
              )}
            >
              <MetricHeader metric={row.metric} />
              {row.values.map((value, colIndex) => (
                <td
                  key={colIndex}
                  className={cn(
                    'p-3 text-center',
                    row.bestIndex === colIndex && 'bg-primary/5'
                  )}
                >
                  <MetricCell
                    value={value}
                    metric={row.metric}
                    isBest={row.bestIndex === colIndex}
                    isHighlighted={differentIndices.has(rowIndex)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Metric header component
 */
function MetricHeader({ metric }: { metric: ComparisonRow['metric'] }) {
  const icon = useMemo(() => {
    switch (metric.key) {
      case 'vendorType':
        return <Leaf className="h-4 w-4" />;
      case 'certifications':
        return <Award className="h-4 w-4" />;
      case 'seasonalAvailability.yearRound':
        return <Calendar className="h-4 w-4" />;
      case 'farmingPractices':
        return <Leaf className="h-4 w-4" />;
      case 'deliveryOptions':
        return <Truck className="h-4 w-4" />;
      case 'csaProgram.available':
        return <Home className="h-4 w-4" />;
      case 'socialProof.googleRating':
        return <Star className="h-4 w-4" />;
      case 'socialProof.reviewCount':
        return <Star className="h-4 w-4" />;
      case 'education.farmTours':
        return <Check className="h-4 w-4" />;
      default:
        return null;
    }
  }, [metric.key]);

  return (
    <th className="sticky left-0 bg-background z-10 p-3 text-left border-r min-w-[150px]">
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-medium text-sm">{metric.label}</span>
      </div>
    </th>
  );
}
